import { useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

export const VALID_CATEGORIES = ['pii', 'prompt_injection', 'banned_topics', 'toxicity', 'intent'];
export const VALID_ACTIONS = ['allow', 'block', 'partial'];
export const VALID_RANGES = ['15m', '1h', '24h', '7d', '30d', 'custom'];
export const DEFAULT_RANGE = '24h';
// Slugs the extractor emits today. The broader PROVIDER_LABEL map in
// findings/providerIcon.js is a superset used for rendering; the filter
// only offers what real scans can actually be labelled with.
export const VALID_PROVIDERS = ['openai', 'anthropic', 'gemini', 'unknown', 'json_parse'];
export const VALID_SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];

const WIRE_KEY = {
    apiKeyIds:     'account_id',
    actions:       'action',
    categories:    'categories',
    correlationId: 'correlation_id',
    tenantId:      'tenant_id',
    userId:        'user_id',
    contentType:   'content_type',
    pageSize:      'page_size',
    providers:     'provider',
    severities:    'severity',
    minConfidence: 'min_confidence',
};

function pickAllowed(values, allowed, key) {
    return values.filter((v) => {
        const ok = allowed.includes(v);
        if (!ok) console.warn(`useScanFilters: dropping invalid ${key} value "${v}"`);
        return ok;
    });
}

export function useScanFilters() {
    const history = useHistory();
    const { pathname, search } = useLocation();

    // Track latest search in a ref so callbacks don't recreate on every search change
    const searchRef = useRef(search);
    useLayoutEffect(() => {
        searchRef.current = search;
    });

    const filters = useMemo(() => {
        const p = new URLSearchParams(search);
        const rangeRaw = p.get('range') || DEFAULT_RANGE;
        return {
            range: VALID_RANGES.includes(rangeRaw) ? rangeRaw : DEFAULT_RANGE,
            from: p.get('from') || null,
            to: p.get('to') || null,
            apiKeyIds: p.getAll('account_id'),
            actions: pickAllowed(p.getAll('action'), VALID_ACTIONS, 'action'),
            categories: pickAllowed(p.getAll('categories'), VALID_CATEGORIES, 'categories'),
            correlationId: p.get('correlation_id') || '',
            status: p.get('status') || '',
            tenantId: p.get('tenant_id') || '',
            userId: p.get('user_id') || '',
            contentType: p.get('content_type') || '',
            providers: pickAllowed(p.getAll('provider'), VALID_PROVIDERS, 'provider'),
            severities: pickAllowed(p.getAll('severity'), VALID_SEVERITIES, 'severity'),
            minConfidence: p.get('min_confidence') != null ? Number(p.get('min_confidence')) : null,
            page: Number(p.get('page')) || 1,
            pageSize: Number(p.get('page_size')) || 50,
        };
    }, [search]);

    useEffect(() => {
        const p = new URLSearchParams(searchRef.current);
        if (!p.get('range')) {
            p.set('range', DEFAULT_RANGE);
            history.replace(`${pathname}?${p.toString()}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setFilter = useCallback(
        (key, value) => {
            const p = new URLSearchParams(searchRef.current);
            if (key !== 'page' && key !== 'pageSize') p.delete('page');

            const wireKey = WIRE_KEY[key] || key;

            if (Array.isArray(value)) {
                p.delete(wireKey);
                value.forEach((v) => p.append(wireKey, v));
            } else if (value === '' || value == null) {
                p.delete(wireKey);
            } else {
                p.set(wireKey, String(value));
            }
            // Eagerly update the ref so sequential synchronous calls see the latest
            // search string rather than the pre-render snapshot.
            searchRef.current = `?${p.toString()}`;
            history.replace(`${pathname}${searchRef.current}`);
        },
        [history, pathname],
    );

    const clearAll = useCallback(() => {
        const p = new URLSearchParams();
        p.set('range', DEFAULT_RANGE);
        const openId = new URLSearchParams(searchRef.current).get('open');
        if (openId) p.set('open', openId);
        history.replace(`${pathname}?${p.toString()}`);
    }, [history, pathname]);

    return { filters, setFilter, clearAll };
}
